import { useRef, useState } from "react"
import { useParams, useNavigate } from "react-router-dom"
import { Link, Button, Form, HStack, Message, Panel, PanelGroup, Schema } from "rsuite"
import { useTranslation } from 'react-i18next'
import toastMessage, { toastError } from '../utils/toastMessage'
import Format, { PasswordField } from "./FormControls"
import { resetPassword } from '@client/utils/db'
import Overlay from "./Overlay"

const { StringType } = Schema.Types

const formStyle: React.CSSProperties = {
    backgroundColor: 'var(--color-2)',
    maxWidth: '90vw',
}
const messageLinkStyle: React.CSSProperties = {
    marginLeft: '1em',
    color: '#ccc'
}

type FormDataProps = { password: string, confirmPassword: string }

export default function ResetPassword() {
    const { t } = useTranslation()
    const navigate = useNavigate()
    const format = Format()
    const formRef = useRef<FormDataProps>({ password: '', confirmPassword: '' })
    const [formData, setFormData] = useState<FormDataProps>({ password: '', confirmPassword: '' })
    const [error, setError] = useState<any>('')
    const [loading, setLoading] = useState(false)
    const { code } = useParams()

    async function handleSubmit(n: any, e: any) {
        e?.preventDefault()

        try {
            setError('')
            setLoading(true)

            if (code) {
                await resetPassword(code, formData.password, formData.confirmPassword)
                toastMessage(t('You have successfully changed your password.'))
                setTimeout(() => {
                    // call main page -> login
                    navigate("/")
                }, 1000)
            } else {
                setError(t('Could not update the password.') + "\n" + t('Please try the link again from your email.'))
            }
        } catch (err: any) {
            if (err.response?.data?.message) {
                const data = err.response.data
                setError(
                    <>
                        {t(data.message)}
                        {data.link && data.linkText && <Link href={data.link} style={messageLinkStyle}>{t(data.linkText)}</Link>}
                    </>
                )
            }
            else setError(t(err.message))
        }
        finally {
            setLoading(false)
        }
    }

    const model = Schema.Model({
        password: StringType()
            .when((schema) => {
                return StringType()
            })
            .addRule(
                (value, data) => {
                    return true
                },
                t('Passwords not match')
            ),
        confirmPassword: StringType()
            .when((schema) => {
                return schema.password.value
                    ? StringType().isRequired(format.required(t('Confirm password')))
                    : StringType()
            })
            .addRule((value, data) => value === data.password, t('Passwords no match') as string),
    })
    return (
        <Overlay>
            <Panel header={t('Reset your password')} bordered style={formStyle}>
                <PanelGroup>
                    {error && <Message type="error">{error}</Message>}

                    <Form
                        id="reset-password-form"
                        layout="vertical"
                        ref={formRef as any}
                        onChange={setFormData as any}
                        formValue={formData}
                        model={model}
                        onSubmit={handleSubmit}
                    >
                        <PasswordField name="password" label={t('New password')} autoFocus={true} autoComplete='new-password' />
                        <PasswordField name="confirmPassword" label={t('Confirm password')} autoComplete='new-password' />

                        <HStack align="center" spacing={20} style={{ marginTop: 20, width: '100%' }}>
                            <Button type="submit" appearance='ghost' disabled={loading}>{t('Reset password')}</Button>
                        </HStack>
                    </Form>
                </PanelGroup>
            </Panel>
        </Overlay>
    )
}